import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../utils/supabase";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

interface ExpiryStatusData {
  total_items: number;
  expired_items: number;
  expiring_soon: number;
  upcoming_items: number;
  hazardous_expired: number;
}

interface ExpiryCheckSession {
  id: string;
  check_date: string;
  checked_by: string;
  is_completed: boolean;
  total_compounding_materials_checked: number;
  total_specialties_checked: number;
  total_expired_found: number;
}

const ExpiryStatus: React.FC = () => {
  const { selectedPharmacy } = useAuth();
  const [statusData, setStatusData] = useState<ExpiryStatusData | null>(null);
  const [lastCheckSession, setLastCheckSession] = useState<ExpiryCheckSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedPharmacy) {
      fetchExpiryStatus();
    }
  }, [selectedPharmacy]);

  const fetchExpiryStatus = async () => {
    if (!selectedPharmacy) return;

    try {
      setLoading(true);

      // Get expiry summary using the database function
      const { data: summaryData, error: summaryError } = await supabase
        .rpc("get_expiry_summary", { pharmacy_uuid: selectedPharmacy.id });

      if (summaryError) {
        console.error("Error fetching expiry summary:", summaryError);
        return;
      }

      if (summaryData && summaryData.length > 0) {
        setStatusData(summaryData[0]);
      }

      // Get the most recent expiry check session
      const { data: sessionData, error: sessionError } = await supabase
        .from("expiry_check_sessions")
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("check_date", { ascending: false })
        .limit(1)
        .single();

      if (sessionError && sessionError.code !== "PGRST116") { // PGRST116 = no rows found
        console.error("Error fetching expiry check session:", sessionError);
        return;
      }

      if (sessionData) {
        setLastCheckSession(sessionData);
      }
    } catch (error) {
      console.error("Error fetching expiry status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "good":
        return "text-green-600 bg-green-50";
      case "warning":
        return "text-yellow-600 bg-yellow-50";
      case "critical":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "good":
        return CheckCircleIcon;
      case "warning":
        return ClockIcon;
      case "critical":
        return ExclamationCircleIcon;
      default:
        return CalendarDaysIcon;
    }
  };

  const getOverallStatus = () => {
    if (!statusData) return "unknown";

    if (statusData.expired_items > 0) {
      return "critical";
    } else if (statusData.expiring_soon > 0) {
      return "warning";
    } else {
      return "good";
    }
  };

  const getStatusText = () => {
    if (!statusData) return "Gegevens laden...";

    const overallStatus = getOverallStatus();
    switch (overallStatus) {
      case "good":
        return "Alle producten zijn binnen houdbaarheidsdatum";
      case "warning":
        return `${statusData.expiring_soon} producten verlopen binnenkort`;
      case "critical":
        return `${statusData.expired_items} producten zijn verlopen`;
      default:
        return "Status onbekend";
    }
  };

  const getDaysSinceLastCheck = () => {
    if (!lastCheckSession) return null;

    const lastCheckDate = new Date(lastCheckSession.check_date);
    const today = new Date();
    const daysSince = Math.floor((today.getTime() - lastCheckDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysSince;
  };

  const getNextCheckStatus = () => {
    const daysSince = getDaysSinceLastCheck();
    
    if (daysSince === null) {
      return { status: "never", text: "Nog nooit gecontroleerd", color: "text-red-600" };
    } else if (daysSince >= 30) {
      return { status: "overdue", text: `${daysSince} dagen geleden`, color: "text-red-600" };
    } else if (daysSince >= 20) {
      return { status: "due_soon", text: `${daysSince} dagen geleden`, color: "text-yellow-600" };
    } else {
      return { status: "recent", text: `${daysSince} dagen geleden`, color: "text-green-600" };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!statusData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <CalendarDaysIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p>Geen vervaldatumgegevens beschikbaar</p>
        </div>
      </div>
    );
  }

  const overallStatus = getOverallStatus();
  const StatusIcon = getStatusIcon(overallStatus);
  const nextCheckInfo = getNextCheckStatus();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${getStatusColor(overallStatus)}`}>
            <StatusIcon className="h-6 w-6" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Vervaldatum Controle
            </h3>
            <p className="text-sm text-gray-600">
              {getStatusText()}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {statusData.total_items}
          </div>
          <div className="text-sm text-gray-500">Totaal producten</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-red-600">
            {statusData.expired_items}
          </div>
          <div className="text-xs text-gray-500">Verlopen</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-yellow-600">
            {statusData.expiring_soon}
          </div>
          <div className="text-xs text-gray-500">Verlopen binnenkort</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-green-600">
            {statusData.upcoming_items}
          </div>
          <div className="text-xs text-gray-500">Binnenkort</div>
        </div>
      </div>

      {statusData.hazardous_expired > 0 && (
        <div className="mt-4 p-3 bg-red-50 rounded-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mr-2" />
            <span className="text-sm text-red-800">
              {statusData.hazardous_expired} gevaarlijke materialen verlopen - directe actie vereist
            </span>
          </div>
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CalendarDaysIcon className="h-4 w-4 text-blue-600 mr-2" />
            <span className="text-sm text-blue-800">
              Laatste controle: {nextCheckInfo.text}
            </span>
          </div>
          <div className={`text-xs ${nextCheckInfo.color}`}>
            {nextCheckInfo.status === "never" && "Controle vereist"}
            {nextCheckInfo.status === "overdue" && "Controle vereist"}
            {nextCheckInfo.status === "due_soon" && "Controle binnenkort"}
            {nextCheckInfo.status === "recent" && "Actueel"}
          </div>
        </div>
      </div>

      {lastCheckSession && (
        <div className="mt-4 text-xs text-gray-500">
          <div className="grid grid-cols-2 gap-2">
            <div>
              Grondstoffen: {lastCheckSession.total_compounding_materials_checked}
            </div>
            <div>
              Specialiteiten: {lastCheckSession.total_specialties_checked}
            </div>
          </div>
          {lastCheckSession.total_expired_found > 0 && (
            <div className="mt-1 text-red-600">
              Verlopen gevonden: {lastCheckSession.total_expired_found}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpiryStatus;
